import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import {
  deleteReportDashboardCharts,
  fetchReportCharts,
  fetchReportDashboardCharts,
  patchReportDashboardCharts,
  postReportDashboardCharts,
  reorderReportDashboardCharts,
} from "../../services/httpClient";
import { faClose, faEdit, faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dashboardMap } from "./ui/DashboardMap";
import { Tooltip } from "react-tooltip";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import ReportTimeInterval from "./ui/ReportTimeInterval";

const Dashboard = ({ setCreateDashboard, setActiveKey, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [availableCharts, setAvailableCharts] = useState<any>();
  const [myDashboardCharts, setMyDashboardCharts] = useState<any[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedChart, setSelectedChart] = useState<any>();
  const [mappedChart, setMappedChart] = useState<any>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [draggedProgramId, setDraggedProgramId] = useState<number | null>(null);
  const [errMsg, setErrMsg] = useState("");
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [showDateTimePicker, setShowDateTimePicker] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
    getReportDashboardCharts();
  }, []);

  const getReportCharts = (myCharts: any[]) => {
    fetchReportCharts()
      .then((res: any) => {
        var charts = res[0].data;

        const myChartKeys = new Set(myCharts.map((m: any) => m.chart_key));
        const updated = charts.map((c: any) => ({
          ...c,
          is_existing: myChartKeys.has(c.key),
        }));

        setAvailableCharts(updated);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getReportDashboardCharts = () => {
    fetchReportDashboardCharts()
      .then((res: any) => {
        const myCharts = res[0].data;
        setMyDashboardCharts(myCharts);
        if (myCharts.length === 0) {
          setEditMode(true);
          setCreateDashboard(true);
        } else {
          setCreateDashboard(false);
          myCharts.map((chart: any) => {
            const usePicker = dashboardMap[chart.chart_key]?.useDateTimePicker;
            if (usePicker) {
              setShowDateTimePicker(true);
              return true;
            }
            return false;
          });
        }

        setErrMsg("");
        getReportCharts(myCharts);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const updateSelectedChart = (e: any) => {
    setSelectedChart(e);
    setMappedChart(dashboardMap[e?.key]);
  };

  const onClickAddChart = () => {
    const newChart = {
      chart_key: selectedChart?.key,
      col_size: mappedChart.defaultCols,
    };

    postReportDashboardCharts(newChart)
      .then((res: any) => {
        setSelectedChart(null);
        setMappedChart({});
        setShowDateTimePicker(false);
        getReportDashboardCharts();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickDoneEdit = () => {
    setSelectedChart(null);
    setMappedChart(null);
    setErrMsg("");

    if (myDashboardCharts.length === 0) {
      setActiveKey("interaction");
    } else {
      setEditMode((prev: any) => !prev);
    }
  };

  const onClickDeleteChart = (id: number) => {
    deleteReportDashboardCharts(id)
      .then((res: any) => {
        setShowDateTimePicker(false);
        getReportDashboardCharts();
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickExpandChart = (chart: any, size: string) => {
    patchReportDashboardCharts(chart?.id, size)
      .then((res: any) => {
        getReportDashboardCharts();
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const reordered = [...myDashboardCharts];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    updateChartOrder(reordered);
  };

  const updateChartOrder = (newOrder: any) => {
    reorderReportDashboardCharts(newOrder)
      .then(() => {
        setDraggedIndex(null);
        getReportDashboardCharts();
      })
      .catch((err) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  return (
    <>
      {isLoading ? (
        <Container className="div-main">
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <div id="dashboard" className="mb-5">
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row id="dashboard-title" className="mb-3">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">{editMode ? "Edit Dashboard" : "Dashboard"}</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                  {editMode ? "Close" : <FontAwesomeIcon icon={faEdit} />}
                </Button>
              )}
            </Col>
          </Row>
          {editMode && (
            <>
              <Row id="programs-filters" className="mb-3">
                <Col></Col>
                <Col xs={12} lg={7} className="mb-1 d-flex align-items-center justify-content-center">
                  <Stack direction="horizontal" gap={3} className="w-100">
                    <SelectObjectTypeahead
                      label="Available Charts or Metrics"
                      value={selectedChart}
                      options={availableCharts}
                      onUpdate={(e: any) => updateSelectedChart(e)}
                      cssClasses="w-75 z-2"
                    />

                    <Button
                      className="app-tertiary-color button w-25"
                      disabled={selectedChart ? false : true}
                      onClick={() => onClickAddChart()}
                    >
                      Add To Dashboard
                    </Button>
                  </Stack>
                </Col>
                <Col></Col>
              </Row>
              {mappedChart && (
                <Row className="mb-3">
                  <div className="d-flex align-items-center justify-content-center">
                    <Col key={selectedChart?.key} xs={mappedChart?.defaultCols}>
                      {mappedChart?.component && <mappedChart.component {...mappedChart?.defaultProps} />}
                    </Col>
                  </div>
                </Row>
              )}
              <Row className="mt-3">
                <hr />
              </Row>
            </>
          )}
          {!editMode && showDateTimePicker && (
            <Row>
              <Col>
                <ReportTimeInterval
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  dateInterval={dateInterval}
                  setDateInterval={setDateInterval}
                  intervalOne={30}
                  intervalOneText={"30 Days"}
                  intervalTwo={90}
                  intervalTwoText={"90 Days"}
                  intervalThree={180}
                  intervalThreeText={"180 Days"}
                  intervalFour={365}
                  intervalFourText={"Last Year"}
                  onClickRefresh={getReportDashboardCharts}
                  hideBtnId="dashboard-refresh-button"
                />
              </Col>
            </Row>
          )}
          <div className="dashboard-masonry">
            {myDashboardCharts?.map((chart: any, index: number) => {
              const mappedChart = dashboardMap[chart.chart_key];
              if (!mappedChart) return <div>Unknown Chart</div>;

              const { component: ChartComponent, defaultCols, maxCols, defaultProps } = mappedChart;

              return (
                <div
                  key={chart?.id}
                  style={{
                    gridColumn: `span ${chart?.col_size}`,
                    padding: ".5rem",
                    alignSelf: "start",
                  }}
                >
                  {editMode ? (
                    <div className="position-relative">
                      <Button
                        data-tooltip-id="tooltip-remove"
                        data-tooltip-content="Remove"
                        className="app-tertiary-color-inverse button position-absolute top-0 end-0 me-3 mt-3 z-1"
                        onClick={() => onClickDeleteChart(chart.id)}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </Button>
                      <Tooltip id="tooltip-remove" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                      <div
                        data-tooltip-id="tooltip-chart"
                        data-tooltip-content="Drag to Move"
                        className={dropTargetIndex === index ? "drop-target" : "dashboard-edit"}
                        key={chart.id}
                        draggable
                        onDragStart={(e) => {
                          setDraggedIndex(index);
                          setDraggedProgramId(chart.id);
                          e.currentTarget.classList.add("dragging");
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (draggedProgramId !== chart.id) {
                            setDropTargetIndex(index);
                          } else {
                            setDropTargetIndex(null);
                          }
                        }}
                        onDragLeave={() => setDropTargetIndex(null)}
                        onDrop={() => {
                          if (draggedProgramId !== chart.id) {
                            handleDrop(index);
                          }
                          setDropTargetIndex(null);
                          setDraggedProgramId(null);
                        }}
                        onDragEnd={() => {
                          document.querySelectorAll(".dragging").forEach((el) => el.classList.remove("dragging"));
                          setDraggedIndex(null);
                          setDraggedProgramId(null);
                          setDropTargetIndex(null);
                        }}
                      >
                        <ChartComponent {...defaultProps} />
                      </div>
                      <Tooltip id="tooltip-chart" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                      <Button
                        data-tooltip-id="tooltip-expand"
                        data-tooltip-content="Grow/Shrink"
                        className="app-tertiary-color-inverse button position-absolute bottom-0 end-0 me-3 mb-3 z-1"
                        onClick={() => onClickExpandChart(chart, chart.col_size == defaultCols ? maxCols : defaultCols)}
                      >
                        <FontAwesomeIcon icon={faExpand} />
                      </Button>
                      <Tooltip id="tooltip-expand" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    </div>
                  ) : (
                    <div className="position-relative">
                      <div key={index}>
                        <ChartComponent {...defaultProps} startDate={startDate} endDate={endDate} useDatePicker={true} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
